import React, { useEffect, useRef } from "react";
import Popup from "reactjs-popup";
import type { PopupPosition } from "reactjs-popup/dist/types";

interface MyPopupProps {
  trigger: React.ReactElement;
  children: (args: { close: () => void }) => React.ReactNode;
  popUpDirection?: PopupPosition | PopupPosition[];
  on?: "hover" | "click" | "focus" | ("hover" | "click" | "focus")[];
}

const MyPopup: React.FC<MyPopupProps> = ({
  trigger,
  children,
  popUpDirection = "top center",
  on = "click",
}) => {
  const popupRef = useRef<any>(null);

  const handleClose = () => {
    if (popupRef.current && typeof popupRef.current.close === "function") {
      popupRef.current.close();
    }
  };

  const renderContent = (close: () => void) => {
    const combinedClose = () => {
      close?.();
      handleClose();
    };
    return children({ close: combinedClose });
  };

  // ...inside MyPopup component, after popupRef declaration...

  useEffect(() => {
    const adjustPosition = () => {
      try {
        // let reactjs-popup recompute first
        popupRef.current?.reposition?.();

        // popup content created by reactjs-popup often has class `popup-content`
        const popupContent = document.querySelector<HTMLElement>(
          ".mail-inbox-popup .popup-content, .mail-inbox-popup-content, .mail-inbox-popup > .popup-content",
        );
        if (!popupContent) return;

        const rect = popupContent.getBoundingClientRect();
        const margin = 8; // keep a small margin from viewport edges

        // If right edge overflows, shift left
        if (rect.right > window.innerWidth - margin) {
          const overflow = rect.right - (window.innerWidth - margin);
          const currentLeft = parseFloat(
            getComputedStyle(popupContent).left || `${rect.left}`,
          );
          popupContent.style.left = `${Math.max(margin, currentLeft - overflow)}px`;
        }

        // If left edge overflows (negative), nudge right
        if (rect.left < margin) {
          popupContent.style.left = `${margin}px`;
        }
      } catch (e) {
        // ignore
      }
    };

    // Run on resize/scroll
    const handler = () => adjustPosition();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);

    // Watch DOM for popup being added (reactjs-popup mounts content on open)
    const observer = new MutationObserver(() => {
      // slight delay to let popup render then adjust
      setTimeout(adjustPosition, 0);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
      observer.disconnect();
    };
  }, []);

  return (
    <Popup
      ref={popupRef}
      trigger={trigger}
      on={on}
      closeOnDocumentClick
      repositionOnResize
      position={popUpDirection}
      className="mail-inbox-popup"
    >
      {renderContent as any}
    </Popup>
  );
};

export default MyPopup;
